import React from 'react';
import type { TimeEntry, Expense, LineItemOverride } from '../types/billing.types';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

interface InvoiceTotalsBarProps {
  timeEntries: TimeEntry[];
  expenses: Expense[];
  selectedTimeEntryIds: Set<string>;
  selectedExpenseIds: Set<string>;
  overrides: Map<string, LineItemOverride>;
}

const InvoiceTotalsBar: React.FC<InvoiceTotalsBarProps> = ({
  timeEntries,
  expenses,
  selectedTimeEntryIds,
  selectedExpenseIds,
  overrides,
}) => {
  const selectedTime = timeEntries.filter((te) => selectedTimeEntryIds.has(te.time_entry_id));
  const selectedExp = expenses.filter((e) => selectedExpenseIds.has(e.expense_id));

  let timeTotal = 0;
  let timeHours = 0;
  let timeOriginalTotal = 0;

  for (const te of selectedTime) {
    const o = overrides.get(te.time_entry_id);
    const qty = o?.billed_quantity ?? Number(te.duration);
    const rate = o?.billed_rate ?? Number(te.rate || 0);
    timeTotal += qty * rate;
    timeHours += qty;
    timeOriginalTotal += Number(te.amount);
  }

  let expenseTotal = 0;
  let expenseOriginalTotal = 0;

  for (const exp of selectedExp) {
    const o = overrides.get(exp.expense_id);
    const qty = o?.billed_quantity ?? Number(exp.quantity);
    const rate = o?.billed_rate ?? Number(exp.unit_cost);
    expenseTotal += qty * rate;
    expenseOriginalTotal += Number(exp.total_amount);
  }

  const adjustments = (timeTotal + expenseTotal) - (timeOriginalTotal + expenseOriginalTotal);
  const estimatedTotal = timeTotal + expenseTotal;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          Selected Time: {selectedTime.length} entries, {timeHours.toFixed(1)} hrs
        </span>
        <span className="font-medium tabular-nums text-gray-900 dark:text-white">{formatCurrency(timeTotal)}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          Selected Expenses: {selectedExp.length} items
        </span>
        <span className="font-medium tabular-nums text-gray-900 dark:text-white">{formatCurrency(expenseTotal)}</span>
      </div>
      {adjustments !== 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-amber-600 dark:text-amber-400">Adjustments</span>
          <span className={`font-medium tabular-nums ${adjustments < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            {adjustments > 0 ? '+' : ''}{formatCurrency(adjustments)}
          </span>
        </div>
      )}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex items-center justify-between">
        <span className="font-medium text-gray-900 dark:text-white">Estimated Total</span>
        <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatCurrency(estimatedTotal)}</span>
      </div>
    </div>
  );
};

export default InvoiceTotalsBar;
